import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
  type ISourceEntity,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISO_DATE_TIME_FORMAT,
  toTimestamp,
  TypeGuard,
  type Timestamp,
  type Timezone,
} from '@principle-theorem/shared';
import { flow, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import { OFFSET_PLACEHOLDER } from '../../../source/source-helpers';
import { PatientSourceEntity } from './patient';

export const PATIENT_AUTOCLAVE_RESOURCE_TYPE = 'patientAutoclave';

export const PATIENT_AUTOCLAVE_SOURCE_ENTITY: ISourceEntity = SourceEntity.init(
  {
    metadata: {
      label: 'Patient Autoclave List',
      description: '',
      idPrefix: PATIENT_AUTOCLAVE_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  }
);

export interface IPraktikaAutoclaveRecord {
  patient_id: number;
  appointment_id: number;
  label: string; // '6/4/19 9424 A | 6/4/19 5765 B | 6/4/19 5767 B'
  created_by: number;
  created_at: string;
}

function isPraktikaAutoclaveRecord(
  item: unknown,
  logError: boolean
): item is IPraktikaAutoclaveRecord {
  return TypeGuard.interface<IPraktikaAutoclaveRecord>(
    {
      patient_id: isNumber,
      appointment_id: isNumber,
      label: isString,
      created_by: isNumber,
      created_at: isString,
    },
    logError
  )(item);
}

export interface IPraktikaPatientAutoclaveFilters {
  appointmentId: string;
  patientId: string;
  date: Timestamp;
}

export interface IPraktikaPatientAutoclaveTranslations {
  date: Timestamp;
}

const PATIENT_AUTOCLAVE_SOURCE_QUERY = `
SELECT sterilisation_record.*, appointment.patient_id FROM (
  SELECT
    convert_to_integer(iAppointmentId) as appointment_id,
    vchLabel as label,
    dtCreated as created_at,
    iCreatedByStaffId as created_by
  FROM autoclave_labels
  ORDER BY iAppointmentId
  ${OFFSET_PLACEHOLDER}
) AS sterilisation_record
LEFT JOIN (
  SELECT
    iAppointmentId AS id,
    iPatientNumber AS patient_id
  FROM appointments
) AS appointment
ON sterilisation_record.appointment_id = appointment.id
WHERE appointment.patient_id IS NOT NULL
`;

const PATIENT_AUTOCLAVE_ESTIMATE_QUERY = `
SELECT sterilisation_record.* FROM (
  SELECT
    convert_to_integer(iAppointmentId) as appointment_id,
    vchLabel as label,
    dtCreated as created_at
  FROM autoclave_labels
) AS sterilisation_record
LEFT JOIN (
  SELECT iAppointmentId as id, iPatientNumber as patient_id FROM appointments
) AS appointment
ON sterilisation_record.appointment_id = appointment.id
WHERE appointment.patient_id IS NOT NULL
GROUP BY appointment_id, label, created_at
`;

export type PraktikaAutoclaveRecord = ISourceEntityTypes<
  IPraktikaAutoclaveRecord,
  IPraktikaPatientAutoclaveTranslations,
  IPraktikaPatientAutoclaveFilters
>;

export class PatientAutoclaveSourceEntity extends BaseSourceEntity<PraktikaAutoclaveRecord> {
  sourceEntity = PATIENT_AUTOCLAVE_SOURCE_ENTITY;
  entityResourceType = PATIENT_AUTOCLAVE_RESOURCE_TYPE;
  sourceQuery = PATIENT_AUTOCLAVE_SOURCE_QUERY;
  override estimateQuery = PATIENT_AUTOCLAVE_ESTIMATE_QUERY;
  verifySourceFn = isPraktikaAutoclaveRecord;
  override transformDataFn = flow([]);

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  translate(
    autoclaveRecord: IPraktikaAutoclaveRecord,
    timezone: Timezone
  ): IPraktikaPatientAutoclaveTranslations {
    return {
      date: toTimestamp(
        moment.tz(autoclaveRecord.created_at, ISO_DATE_TIME_FORMAT, timezone)
      ),
    };
  }

  getSourceRecordId(data: IPraktikaAutoclaveRecord): string {
    return `${data.appointment_id}-${data.label.replace(
      // eslint-disable-next-line no-useless-escape
      /[\/\s:]/g,
      '-'
      // eslint-disable-next-line no-useless-escape
    )}-${data.created_at.replace(/[\/\s:]/g, '-')}`;
  }

  getSourceLabel(data: IPraktikaAutoclaveRecord): string {
    return data.label;
  }

  getFilterData(
    data: IPraktikaAutoclaveRecord,
    timezone: Timezone
  ): IPraktikaPatientAutoclaveFilters {
    return {
      patientId: data.patient_id.toString(),
      appointmentId: data.appointment_id.toString(),
      date: toTimestamp(
        moment.tz(data.created_at, ISO_DATE_TIME_FORMAT, timezone)
      ),
    };
  }
}
